import { Loader2 } from "lucide-react";
import type { OrganizationSelection } from "@/schema";

interface OrganizationAccessScreenProps {
  selection: OrganizationSelection | null;
  error: string | null;
  isChecking: boolean;
  onSelect: (orgId: string) => void;
  onRetry: () => void;
  onSignOut: () => void;
}

const formatRole = (role?: string) => String(role || "member")
  .replace(/^org:/, "")
  .replace(/_/g, " ");

export function OrganizationAccessScreen({
  selection,
  error,
  isChecking,
  onSelect,
  onRetry,
  onSignOut,
}: OrganizationAccessScreenProps) {
  const orgs = selection?.options.orgs.items || [];
  const requiresChoice = orgs.length > 1;

  return (
    <div className="grid min-h-screen place-items-center p-6">
      <div className="grid w-full max-w-[460px] gap-4 text-center">
        <h1 className="m-0 text-2xl font-semibold text-[var(--text-primary)]">
          {requiresChoice ? "Choose an organization" : "Organization access required"}
        </h1>
        <p className="m-0 leading-6 text-[var(--text-secondary)]">
          {requiresChoice
            ? "Select the organization you want to use. Your choice becomes the active organization for this browser."
            : "No organization is available yet. Access is checked automatically after an administrator grants it."}
        </p>

        {requiresChoice && (
          <div
            className="overflow-hidden rounded-xl border bg-[var(--bg-secondary)] text-left shadow-2xl"
            style={{ borderColor: "var(--border-primary)" }}
            data-organization-selection
          >
            {orgs.map((org) => (
              <button
                key={org.id}
                type="button"
                disabled={isChecking}
                onClick={() => onSelect(org.id)}
                className="flex w-full items-center justify-between gap-4 border-b px-4 py-3 text-left transition last:border-b-0 hover:bg-[var(--bg-tertiary)] disabled:cursor-wait disabled:opacity-60"
                style={{ borderColor: "var(--border-primary)" }}
                data-org-id={org.id}
              >
                <span className="min-w-0">
                  <span className="block truncate text-sm font-semibold text-[var(--text-primary)]">
                    {org.name || org.id}
                  </span>
                  <span className="block truncate text-xs text-[var(--text-muted)]">
                    {org.id}
                  </span>
                </span>
                <span className="flex shrink-0 items-center gap-2 text-[10px] uppercase tracking-wide text-[var(--text-muted)]">
                  {org.managed || org.inherited ? "Managed · " : ""}
                  {formatRole(org.orgRole)}
                  {isChecking ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                </span>
              </button>
            ))}
          </div>
        )}

        {error && <div className="text-sm text-red-400">{error}</div>}

        {!requiresChoice && (
          <button
            type="button"
            onClick={onRetry}
            disabled={isChecking}
            className="rounded-[10px] border-0 bg-[var(--accent-primary)] px-[22px] py-[10px] text-sm font-semibold text-white disabled:cursor-wait disabled:opacity-70"
          >
            {isChecking ? "Checking access…" : "Check access again"}
          </button>
        )}

        <button
          type="button"
          onClick={onSignOut}
          className="rounded-[10px] border bg-transparent px-[22px] py-[10px] text-sm font-semibold text-[var(--text-primary)]"
          style={{ borderColor: "var(--border-primary)" }}
        >
          Sign out and use another account
        </button>
      </div>
    </div>
  );
}
